<template>
  <div
    v-if="isFloating"
    v-appendTo="appendToEditor ? getEditorRef() : (appendTo || undefined)"
    :id="id"
    :editor-floating-menu-module-id="moduleId"
    :style="menuStyle"
    class="editor-menu editor-menu--floating"
  >
    <slot />
  </div>
  <div
    v-else
    class="editor-menu"
  >
    <slot />
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';
import {
  assign, cloneDeep, isPlainObject, pick, throttle,
} from 'lodash-es';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import { bus } from '@/lib/eventBus';
import appendTo from '@/directives/appendTo';

const nebulaSpace1x = parseInt(nebulaTokens.space['1x'], 10);
const nebulaSpace2x = parseInt(nebulaTokens.space['2x'], 10);
const nebulaSpace7x = parseInt(nebulaTokens.space['7x'], 10);
const nebulaBreakpointTabletPortrait = parseInt(nebulaTokens.breakpoints['tablet-portrait'], 10);

export default {
  props: {
    idPrefix: {
      type: String,
      default: 'editor-floating-menu',
    },
    moduleId: {
      type: String,
      default: null,
    },
    scale: {
      type: Number,
      default: 1,
    },
    updateMenuPositionDelay: {
      type: Number,
      default: 0,
    },
    triggerElement: {
      type: HTMLElement,
      default: null,
    },
    triggerElementIdHorizontal: {
      type: String,
      default: null,
    },
    triggerElementIdVertical: {
      type: String,
      default: null,
    },
    triggerAdditionalElementIds: {
      type: Array,
      default: () => [],
    },
    triggerPadding: {
      type: Number,
      default: nebulaSpace2x,
    },
    positions: {
      type: Array,
      default: () => ['bottom'], // top, bottom, left, right
    },
    boxName: {
      type: String,
      default: 'editor', // editor, notes
    },
    isFloating: {
      type: Boolean,
      default: true,
    },
    appendTo: {
      type: HTMLElement,
      default: null,
    },
    appendToEditor: {
      type: Boolean,
      default: false,
    },
    containMenuHorizontal: {
      type: Boolean,
      default: true,
    },
    containMenuVertical: {
      type: Boolean,
      default: true,
    },
    inZoomContainer: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['position-changed'],
  directives: {
    appendTo,
  },
  inject: {
    getEditorRef: {
      default() {
        return () => null;
      },
    },
  },
  data() {
    return {
      idGuid: uuid(),
      left: null,
      top: null,
      transform: null,
      nebulaSpace1x,
      nebulaSpace2x,
      nebulaSpace7x,
    };
  },
  created() {
    if (!this.isFloating) return;
    this.updateMenuPositionThrottled = throttle(() => {
      setTimeout(this.updateMenuPosition.bind(this), this.updateMenuPositionDelay);
    }, 50);
  },
  mounted() {
    if (!this.isFloating) return;
    this.editorBoxEl = this.getEditorBox();
    this.notesBoxEl = this.getNotesBox();
    this.notesScrollBoxEl = this.getNotesScrollBox();
    this.moduleBoxEl = this.getModuleBox();
    this.zoomContainerEl = this.getZoomContainer();
    if (this.zoomContainerEl) {
      /*
        Get the current border applied to the zoom container to be used
        when adjusting box dimensions for the zoom container.
      */
      const zoomStyles = getComputedStyle(this.zoomContainerEl);
      this.zoomBorderInlineStartWidth = zoomStyles?.borderInlineStartWidth
        ? parseInt(zoomStyles.borderInlineStartWidth, 10)
        : 0;
      this.zoomBorderBlockStartWidth = zoomStyles?.borderBlockStartWidth
        ? parseInt(zoomStyles.borderBlockStartWidth, 10)
        : 0;
    }

    this.updateMenuPositionThrottled();

    // Reposition the editor menu in response to various events while using the editor.
    bus.on('update-module-options', this.updateMenuPositionThrottled);
    window.addEventListener('resize', this.updateMenuPositionThrottled);
    bus.on('moved', this.onModuleUpdated);
    bus.on('resized', this.onModuleUpdated);
    bus.on('window:scroll', this.updateMenuPositionThrottled);
    bus.on('module:move', this.onModuleUpdated);
    bus.on('module:resize', this.onModuleUpdated);
    bus.on('module:updated', this.onModuleUpdated);
    bus.on('module:auto-resizing-y', this.onModuleUpdated);
    bus.on('editor:zoom-changed', this.updateMenuPositionThrottled);
    bus.on('editor:resized', this.updateMenuPositionThrottled);
    if (this.editorBoxEl) this.editorBoxEl.addEventListener('scroll', this.updateMenuPositionThrottled);
    if (this.notesScrollBoxEl) this.notesScrollBoxEl.addEventListener('scroll', this.updateMenuPositionThrottled);
    if (this.moduleBoxEl) this.moduleBoxEl.addEventListener('scroll', this.updateMenuPositionThrottled);

    this.menuResizeObserver = new ResizeObserver(this.updateMenuPosition);
    this.menuResizeObserver.observe(this.$el);
  },
  beforeUnmount() {
    if (!this.isFloating) return;
    bus.off('update-module-options', this.updateMenuPositionThrottled);
    window.removeEventListener('resize', this.updateMenuPositionThrottled);
    bus.off('moved', this.onModuleUpdated);
    bus.off('resized', this.onModuleUpdated);
    bus.off('window:scroll', this.updateMenuPositionThrottled);
    bus.off('module:move', this.onModuleUpdated);
    bus.off('module:resize', this.onModuleUpdated);
    bus.off('module:updated', this.onModuleUpdated);
    bus.off('module:auto-resizing-y', this.onModuleUpdated);
    bus.off('editor:zoom-changed', this.updateMenuPositionThrottled);
    bus.off('editor:resized', this.updateMenuPositionThrottled);
    if (this.editorBoxEl) this.editorBoxEl.removeEventListener('scroll', this.updateMenuPositionThrottled);
    if (this.notesScrollBoxEl) this.notesScrollBoxEl.removeEventListener('scroll', this.updateMenuPositionThrottled);
    if (this.moduleBoxEl) this.moduleBoxEl.removeEventListener('scroll', this.updateMenuPositionThrottled);

    if (this.menuResizeObserver) {
      this.menuResizeObserver.unobserve(this.$el);
      this.menuResizeObserver = null;
    }
  },
  computed: {
    id() {
      return `${this.idPrefix}-${this.moduleId || this.idGuid}`;
    },
    menuStyle() {
      const style = {
        left: this.left,
        top: this.top,
        transform: this.transform,
      };

      if (!style.top || !style.left) {
        // Keep menu hidden until coordinates are set
        style.visibility = 'hidden';
      }

      return style;
    },
  },
  methods: {
    getEditorBox() {
      return this.getEditorRef()?.querySelector('.modal-canvas__stage')
        || this.getEditorRef()?.querySelector('.editor__stage')
        || document.body;
    },
    getNotesBox() {
      return this.getEditorRef()?.querySelector('.notes-canvas__scroll-wrap')
        || document.body;
    },
    getNotesScrollBox() {
      return this.getEditorRef()?.querySelector('.notes-canvas__modules-scroll-wrap')
        || document.body;
    },
    getZoomContainer() {
      // If a modal canvas is open, use that as the zoom container.
      // Otherwise use the Editor zoom container
      const editorRef = this.getEditorRef() || document.body;

      return editorRef.querySelector('.modal-canvas__inner-wrap')
        || editorRef.querySelector('.editor__zoom-content');
    },
    getModuleBox() {
      if (!this.moduleId) return null;

      return (this.getEditorRef() || document.body).querySelector(`[id='${this.moduleId}'] .component-module-wrapper__module-contents`);
    },
    createBox(el, isTriggerElement = false) {
      const {
        left, top, bottom, right, width, height,
      } = el.getBoundingClientRect();

      const box = {
        el,
        left,
        top,
        width,
        height,
        right: isTriggerElement ? right : left + el.clientWidth,
        bottom: isTriggerElement ? bottom : top + el.clientHeight,
      };

      /*
        If this is the trigger element and there are other elements
        that should be grouped with the trigger element, then extend
        the bounding box of the trigger element to wrap around all elements
        in the group.
      */
      if (isTriggerElement) {
        this.triggerAdditionalElementIds.forEach((elementId) => {
          const element = document.getElementById(elementId);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top < box.top) box.top = rect.top;
            if (rect.bottom > box.bottom) box.bottom = rect.bottom;
            box.height = box.bottom - box.top;
          }
        });
      }

      return box;
    },
    adjustBoxForZoomContainer(box) {
      /*
        The zoom container uses transform which creates it's own positioning context.
        Adjust the provided boundary box to account for the zoom container's position
        context.
      */
      if (!this.zoomContainerEl) return box;
      const adjusted = cloneDeep(box);
      const zoomRect = this.zoomContainerEl.getBoundingClientRect();
      const leftAdjustment = zoomRect.left + this.zoomBorderInlineStartWidth * this.scale;
      const topAdjustment = zoomRect.top + this.zoomBorderBlockStartWidth * this.scale;
      adjusted.left -= leftAdjustment;
      adjusted.top -= topAdjustment;
      adjusted.right -= leftAdjustment;
      adjusted.bottom -= topAdjustment;
      return adjusted;
    },
    createBoundariesForChildBox(box, childBox, horizontalPadding = 0, verticalPadding = 0) {
      // Create boundaries for the child box to fit inside the parent.
      return {
        minTop: box.top,
        maxTop: box.bottom - childBox.height - verticalPadding,
        maxLeft: box.right - childBox.width - horizontalPadding,
        minLeft: box.left + horizontalPadding,
      };
    },
    createMenuInfo(
      boundaryBox,
      triggerBox,
      menuBox,
      horizontalPadding = 0,
      verticalPadding = 0,
    ) {
      const info = {
        boundaries: this.createBoundariesForChildBox(boundaryBox, menuBox,
          horizontalPadding, verticalPadding),
      };

      const {
        maxLeft, minLeft, maxTop, minTop,
      } = info.boundaries;

      // Calculate preferred positions for the menu relative to the trigger element.
      const menuLeftForBottomAndTop = triggerBox.left + (triggerBox.width / 2 - menuBox.width / 2);
      const menuLeftForLeft = triggerBox.left - menuBox.width - this.triggerPadding;
      const menuLeftForRight = triggerBox.right + this.triggerPadding;
      const menuTopForBottom = triggerBox.bottom + this.triggerPadding;
      const menuTopForTop = triggerBox.top - menuBox.height - this.triggerPadding;
      const menuTopForLeftOrRight = triggerBox.top + (triggerBox.height / 2 - menuBox.height / 2);

      info.position = {
        bottom: { left: menuLeftForBottomAndTop, top: menuTopForBottom },
        top: { left: menuLeftForBottomAndTop, top: menuTopForTop },
        left: { left: menuLeftForLeft, top: menuTopForLeftOrRight },
        right: { left: menuLeftForRight, top: menuTopForLeftOrRight },
      };

      // If menu is outside of the boundary box, then adjust so it stays inside.
      Object.keys(info.position).forEach((name) => {
        let { left, top } = info.position[name];

        if (this.containMenuHorizontal) {
          if (left < minLeft) {
            left = minLeft;
            info.position[name].adjustedToFitHorizontal = true;
          } else if (left > maxLeft) {
            left = maxLeft;
            info.position[name].adjustedToFitHorizontal = true;
          }
        }

        if (this.containMenuVertical) {
          if (top < minTop) {
            top = minTop;
            info.position[name].adjustedToFitVertical = true;
          } else if (top > maxTop) {
            top = maxTop;
            info.position[name].adjustedToFitVertical = true;
          }
        }

        // Adjust the positions for the current scale of the canvas.
        info.position[name].left = this.inZoomContainer
          ? left / this.scale : left;
        info.position[name].top = this.inZoomContainer
          ? top / this.scale : top;
      });

      return info;
    },
    getMenuTransform() {
      if (!this.inZoomContainer) return undefined;

      let menuScale = 1; // Actual scale of menu after adjusting for editor scale.

      // Keep menus a constant size regardless of the zoom scale.
      let transformScale = 1 / this.scale;

      // Downsize the menus slightly if the editor window is small.
      if (this.editorBoxEl.clientWidth < nebulaBreakpointTabletPortrait) {
        const downsizePercent = 0.8; // Percentage of actual size menu should be.
        transformScale *= downsizePercent;
        menuScale = downsizePercent;
      }

      return {
        transform: `scale(${transformScale}) translate3d(0, 0, 0)`,
        menuScale,
      };
    },
    updateMenuPosition() {
      this.$nextTick(() => {
        const menuBoxEl = this.$el;
        if (!menuBoxEl || !this.editorBoxEl || !this.triggerElement) return;

        const menuTransform = this.getMenuTransform();
        this.transform = menuTransform?.transform;

        this.$nextTick(() => {
          // Create boundary boxes within the editor to contain the menu within
          const menuBox = this.createBox(menuBoxEl);
          const editorBox = this.createBox(this.editorBoxEl);
          const triggerBox = this.createBox(this.triggerElement, true);

          /*
            If an element is provided to be used for the horizontal or vertical measurements of the
            trigger box, then use that instead. e.g. This is used to place the menu relative to the
            current position of the mini-menu instead of actual trigger element.
          */
          if (this.triggerElementIdHorizontal) {
            const triggerHorizontalBox = document.getElementById(this.triggerElementIdHorizontal);
            if (triggerHorizontalBox) {
              assign(triggerBox, pick(triggerHorizontalBox.getBoundingClientRect(), ['left', 'right', 'width']));
            }
          }
          if (this.triggerElementIdVertical) {
            const triggerVerticalBox = document.getElementById(this.triggerElementIdVertical);
            if (triggerVerticalBox) {
              assign(triggerBox, pick(triggerVerticalBox.getBoundingClientRect(), ['top', 'bottom', 'height']));
            }
          }

          const context = {
            menuBox,
            editorBox,
            triggerBox,
            menuScale: menuTransform?.menuScale,
          };

          // Create the available menu positions within the editor boundary box.
          editorBox.menu = this.createMenuInfo(
            (this.inZoomContainer ? this.adjustBoxForZoomContainer(editorBox) : editorBox),
            (this.inZoomContainer ? this.adjustBoxForZoomContainer(triggerBox) : triggerBox),
            menuBox,
            this.nebulaSpace7x,
          );

          if (this.notesBoxEl) {
            context.notesBox = this.createBox(this.notesBoxEl);
            // Create the available menu positions within the notes boundary box.
            context.notesBox.menu = this.createMenuInfo(
              context.notesBox,
              triggerBox,
              menuBox,
              this.nebulaSpace1x,
            );
          }

          const box = context[`${this.boxName}Box`] || context.editorBox;

          /*
            Multiple menu positions can be provided in preferred order.
            Use the first position with the least number of adjustments needed
            in order to fit inside the boundary box.
          */
          const { position } = this.positions.reduce((acc, pos) => {
            let adjustments = 0;
            if (box.menu.position[pos].adjustedToFitHorizontal) adjustments += 1;
            if (box.menu.position[pos].adjustedToFitVertical) adjustments += 1;
            if (!acc.position || adjustments < acc.adjustments) {
              acc.position = pos;
              acc.adjustments = adjustments;
            }
            return acc;
          }, { position: null, adjustments: 0 });

          const { top, left } = box.menu.position[position];
          this.top = `${top}px`;
          this.left = `${left}px`;

          // Attach the actual boundary box and selected position.
          context.selected = { box, position };

          this.$emit('position-changed', context);
        });
      });
    },
    onModuleUpdated(payload) {
      const moduleId = isPlainObject(payload)
        ? payload.updatedModule.id
        : payload;
      if (moduleId === this.moduleId) {
        this.$nextTick(() => {
          this.updateMenuPositionThrottled();
        });
      }
    },
  },
};
</script>

<style lang="stylus">
.editor-menu {
  position: relative;
  transform-origin: top left;

  &--floating {
    position: fixed;
    z-index: 800;
  }
}
</style>
